import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { Document, OrderDocument } from '../../domain/data';
import { DocumentInterface, DocumentStatus, IdInterface, OrderDocumentInterface } from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse } from './api-client.helpers';

import { environment } from '../../../../environments/environment';

@Injectable()
export class DocumentApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    getContractSignedDocuments(contractCode: IdInterface): Observable<OrderDocumentInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_contract_signed_documents).pipe(
            switchMap((url) => {
                if (environment.portalClient) {
                    return of([]);
                } else {
                    return this.http
                        .post(url, {
                            ContractCode: String(contractCode),
                        })
                        .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, OrderDocument)));
                }
            })
        );
    }

    getDocuments(type: string, contractCode?: IdInterface, clientCode?: IdInterface): Observable<DocumentInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_documents).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        ClientCode: type === 'Client' ? String(clientCode) : undefined,
                        ContractCode: (type === 'Client' && contractCode) || type === 'Contract' ? String(contractCode) : undefined,
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Document)));
            })
        );
    }

    getDocumentDetail(document: DocumentInterface): Observable<any> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_document).pipe(
            switchMap((url) => {
                return this.http.post(url, this.serializer.toJson(document), {
                    responseType: 'blob',
                });
            })
        );
    }

    getOrderDocuments(selectedProductId: IdInterface): Observable<any> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_order_documents).pipe(
            switchMap((url) => {
                return this.http.post(
                    url,
                    {
                        ListID: String(selectedProductId),
                    },
                    { responseType: 'blob' }
                );
            })
        );
    }

    getProductDocument(productCode: IdInterface): Observable<string> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_product_document).pipe(
            switchMap((url) => {
                if (environment.portalClient && !this.localStorage.sessionToken) {
                    return of(undefined);
                } else {
                    return this.http
                        .post(url, {
                            ProductCode: String(productCode),
                        })
                        .pipe(
                            map((resp: any) => {
                                if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                    throw new Error(resp ? resp.Status : 'error.general');
                                } else {
                                    return resp.Base64Document || undefined;
                                }
                            })
                        );
                }
            })
        );
    }

    getTestSignDocuments(clientCode: IdInterface): Observable<any> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_test_sign_documents).pipe(
            switchMap((url) => {
                return this.http.post(
                    url,
                    {
                        ClientCode: String(clientCode),
                    },
                    { responseType: 'blob' }
                );
            })
        );
    }

    removeDocument(document: DocumentInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.remove_document).pipe(
            switchMap((url) => {
                return this.http.post(url, this.serializer.toJson(document)).pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    uploadDocument(code: string, type: string, document: DocumentInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.upload_document).pipe(
            switchMap((url) => {
                const formData: any = new FormData();
                formData.append('Code', code);
                formData.append('Type', type);
                formData.append('TypeId', String(document.typeId));

                if (document.file) {
                    formData.append('Document', document.file, document.file.name);
                }

                return this.http.post(url, formData, { responseType: 'json' }).pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    uploadSignedOrderDocument(selectedProductId: IdInterface, document: File): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.upload_signed_order_document).pipe(
            switchMap((url) => {
                const formData: any = new FormData();
                formData.append('ListId', String(selectedProductId));

                if (document) {
                    formData.append('Document', document, document.name);
                }

                return this.http.post(url, formData, { responseType: 'json' }).pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    uploadTestSignDocument(clientCode: IdInterface, document: File): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.upload_test_sign_document).pipe(
            switchMap((url) => {
                const formData: any = new FormData();
                formData.append('ClientCode', String(clientCode));

                if (document) {
                    formData.append('Document', document, document.name);
                }

                return this.http.post(url, formData, { responseType: 'json' }).pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }

    validateDocument(code: string, type: string, documentId: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.validate_document).pipe(
            switchMap((url) => {
                return this.http
                    .post(url, {
                        Code: code,
                        DocumentID: documentId,
                        Type: type,
                        Validate: String(DocumentStatus.VALIDATE),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)));
            })
        );
    }
}
